import { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import type { FC, ReactNode } from 'react';

import { useSelector } from 'react-redux';

import { Form, Switch } from 'antd';

import { InfoCardList } from '@src/components/InfoCard';
import { DASHBOARD_ITEM_API_NAMES } from '@src/constants/commons';
import TableService from '@src/services/TableService';

import useGetEventTypes from '@src/packages/utils/hooks/useGetEventTypes';

import IntlMessages from '@src/util/IntlMessages';

import {
  AUX_FIELDS_EXTRA,
  AUX_FIELDS_TABLE_FORM,
  DASHBOARDS_EVENT_TYPE,
  INPUT_TYPES_NOT_ALLOWED,
  LAYOUT_BY_COMPONENT_TYPE,
} from '../../../constants';
import { SlidePanelContext } from '@src/packages/dashboard/components/SlidePanel/SlidePanelContext';
import { useEventsByScheduleContext } from '../../../contexts/EventsByScheduleContext';

import type {
  CurrentTableData,
  DashboardEventType,
  EventTypeComponent,
  LocationFormValue,
  TableColumnState,
  TableFormImperativeHandle,
  UseTableFormParams,
  UseTableFormReturn,
} from './useTableForm.types';

const useTableForm = ({
  tableId,
  boardId,
  divId,
}: UseTableFormParams): UseTableFormReturn => {
  const orgId = useSelector(
    (state: { organizations?: { organization?: { id: string } } }) =>
      state.organizations?.organization?.id,
  );
  const { boardSelected } = useContext(SlidePanelContext) as {
    boardSelected?: { divId?: string | null };
  };
  const userOrgDivisions = useSelector(
    (state: {
      divisions?: { assigned?: { data?: { divisions?: Record<string, { id: string; name: string }> } } };
    }) => state.divisions?.assigned?.data?.divisions,
  );
  const {
    loading: schedulesLoading,
    eventsFromSchedules: eventTypesFromSchedule,
    fetchEventsFromSchedules: fetchEventsFromSchedulesRaw,
  } = useEventsByScheduleContext();
  const fetchEventsFromSchedules = fetchEventsFromSchedulesRaw as (
    orgId: string,
    divId: string | undefined,
    eventTypeId: string,
  ) => void | Promise<unknown>;
  const [form] = Form.useForm();
  const watchDivisionId = Form.useWatch('divisionId', form) as string | undefined;
  const tableService = useMemo(() => new TableService(), []);
  const isAOrgBoard = !boardSelected?.divId;

  const columnsFixed = useMemo((): TableColumnState[] => {
    const output: TableColumnState[] = [...(AUX_FIELDS_TABLE_FORM as TableColumnState[])];

    if (isAOrgBoard) {
      output.push({
        componentId: AUX_FIELDS_EXTRA.division,
        isActive: true,
        propertyName: <IntlMessages id="dashboards.events.table.auxFields.division" />,
        order: null,
        index: 0,
      });
    } else {
      const index = output.findIndex(field => field.componentId === AUX_FIELDS_EXTRA.division);
      if (index !== -1) {
        output.splice(index, 1);
      }
    }

    const uniqueOutput = output.filter(
      (field, index, self) => index === self.findIndex(f => f.componentId === field.componentId),
    );

    return uniqueOutput;
  }, [isAOrgBoard]);

  const [tableColumns, setTableColumns] = useState<TableColumnState[]>(() => columnsFixed);
  const [selectedEventTypeId, setSelectedEventTypeId] = useState<string | undefined>();
  const [triggerIds, setTriggerIds] = useState<string[] | null>([]);
  const [currentTable, setCurrentTable] = useState<CurrentTableData>({});
  const [loading, setLoading] = useState(false);

  const { data: eventTypesRaw, loading: eventLoading } = useGetEventTypes(
    true,
    divId || watchDivisionId,
  );
  const eventTypes = eventTypesRaw as DashboardEventType[] | undefined;

  const divisionPerEventType =
    eventTypes?.find(event => event.id === selectedEventTypeId)?.divId ?? null;

  const mainLoading = schedulesLoading || eventLoading || loading;

  const divisions = useMemo(
    () =>
      Object.values(userOrgDivisions || {})
        .map(division => ({
          name: division.name,
          value: division.id,
        }))
        .filter(division => (divId ? division.value === divId : true)),
    [userOrgDivisions, divId],
  );

  const handleDivisionChange = useCallback(() => {
    form.setFieldsValue({
      formId: null,
      triggerIds: [],
      location: null,
    });
    setSelectedEventTypeId(undefined);
    setTriggerIds([]);
  }, [form]);

  const buildColumns = useCallback(
    (components: EventTypeComponent[] = []) => {
      const prevIndex = columnsFixed.length;
      const columns: TableColumnState[] = components.map((component, i) => ({
        componentId: component?.id ?? '',
        isActive: !INPUT_TYPES_NOT_ALLOWED.includes(component?.type ?? ''),
        propertyName: component?.label?.name,
        order: null,
        index: prevIndex + i,
        type: component?.type,
      }));

      setTableColumns(prev => [...prev, ...columns]);
    },
    [columnsFixed.length],
  );

  const buildColumnsFromDataFetched = useCallback(
    (columns: TableColumnState[], formId: string | undefined) => {
      const formFetched = eventTypes?.find(et => et.id === formId);
      const columnsFetched = columns.map(c => ({
        ...c,
        propertyName:
          formFetched?.components.find(comp => comp.id === c.componentId)?.label?.name ||
          columnsFixed.find(aux => aux.componentId === c.componentId)?.propertyName,
      }));

      setTableColumns(columnsFetched);
    },
    [eventTypes, columnsFixed],
  );

  const onFormChange = useCallback(
    (formId?: string) => {
      const formSelected = eventTypes?.find(et => et.id === formId);
      if (!formSelected) return;

      buildColumns(formSelected.components);
    },
    [buildColumns, eventTypes],
  );

  const onChangeTriggers = useCallback(
    (triggers: Array<{ id: string }>) => {
      const newTriggersSelected = triggers.map(tr => tr.id);
      form.setFieldValue('triggerIds', newTriggersSelected);
      setTriggerIds(newTriggersSelected);
    },
    [form],
  );

  const onManageColumns = useCallback(
    (col: TableColumnState) => {
      const newColumns = tableColumns.map(c => {
        if (c.componentId === col.componentId) {
          return { ...c, isActive: !c.isActive };
        }
        return c;
      });

      setTableColumns(newColumns);
    },
    [tableColumns],
  );

  const buildBody = useCallback(() => {
    const title = form.getFieldValue('title');
    const formId = form.getFieldValue('formId');
    const divisionId = form.getFieldValue('divisionId');
    const location = form.getFieldValue('location') as LocationFormValue | undefined;

    const columns = tableColumns.map(record => {
      const { type: _type, propertyName: _propertyName, ...newCol } = record;
      return newCol;
    });

    let locationIdsOrGroup: {
      locationGroupId: string | null;
      locationIds: string[];
    };
    if (location?.isAGroup === true && location?.data && 'id' in location.data) {
      locationIdsOrGroup = {
        locationGroupId: location.data.id,
        locationIds: [],
      };
    } else if (location?.isAGroup === false && Array.isArray(location?.data) && location.data.length) {
      locationIdsOrGroup = {
        locationIds: location.data.map(loc => loc.id),
        locationGroupId: null,
      };
    } else {
      locationIdsOrGroup = {
        locationIds: [],
        locationGroupId: null,
      };
    }

    const body = {
      queryParams: {
        divisionIds: divisionId ? [divisionId] : [],
        triggerIds: triggerIds ?? [],
        columns,
        formId,
        ...locationIdsOrGroup,
      },
      layout: currentTable?.layout ?? LAYOUT_BY_COMPONENT_TYPE[DASHBOARDS_EVENT_TYPE.TABLES],
      title,
      apiName: DASHBOARD_ITEM_API_NAMES.TABLE_DASHBOARD,
    };
    form.setFieldValue('tableFormData', body);
    form.setFieldValue('id', tableId);
  }, [currentTable, form, tableColumns, tableId, triggerIds]);

  const onSortableMove = useCallback((cols: TableColumnState[]) => {
    const columns = cols.map((column, i) => ({ ...column, index: i }));
    setTableColumns(columns);
  }, []);

  const handleClear = useCallback(() => {
    form.setFieldsValue({
      title: '',
      formId: null,
      triggerIds: [],
      divisionId: null,
      location: null,
    });
    setSelectedEventTypeId(undefined);
    setTriggerIds([]);
    setTableColumns(columnsFixed);
  }, [columnsFixed, form]);

  const getImperativeHandle = useCallback((): TableFormImperativeHandle => {
    return {
      ...form,
      handleClear,
      submit: () => {
        form.submit();
      },
    };
  }, [form, handleClear]);

  const checkingIfColumnIsAllowed = useCallback(
    (componentId: string | undefined) => {
      const element = eventTypes
        ?.find(et => et.id === selectedEventTypeId)
        ?.components.find(c => c.id === componentId);
      return element?.type != null && INPUT_TYPES_NOT_ALLOWED.includes(element.type);
    },
    [eventTypes, selectedEventTypeId],
  );

  const ColumnItem = useMemo((): FC<{ data?: TableColumnState }> => {
    const Comp: FC<{ data?: TableColumnState }> = ({ data = {} as TableColumnState }) => {
      const List = InfoCardList as FC<{
        extraRightContent?: ReactNode;
        title?: ReactNode;
      }>;
      return (
        <List
          extraRightContent={
            <div>
              <Switch
                defaultChecked={data?.isActive}
                checked={data?.isActive}
                onChange={() => onManageColumns(data)}
                disabled={checkingIfColumnIsAllowed(data?.componentId)}
              />
            </div>
          }
          title={data?.propertyName}
        />
      );
    };
    return Comp;
  }, [checkingIfColumnIsAllowed, onManageColumns]);

  const handleFormIdChange = useCallback(
    (val: string | undefined) => {
      form.setFieldValue('triggerIds', []);
      setTriggerIds([]);
      setTableColumns(columnsFixed);

      setSelectedEventTypeId(val);
      onFormChange(val);
    },
    [columnsFixed, form, onFormChange],
  );

  const handleTriggersClear = useCallback(() => {
    form.setFieldValue('triggerIds', []);
    setTriggerIds(null);
  }, [form]);

  useEffect(() => {
    buildBody();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableColumns]);

  useEffect(() => {
    if (tableId && boardId) {
      const fetchData = async () => {
        setLoading(true);
        const res = (await tableService.getTable({ orgId, divId, boardId, tableId })) as
          | CurrentTableData
          | null;
        if (res) setCurrentTable(res);
        setLoading(false);
      };
      fetchData();
    }
  }, [boardId, divId, orgId, tableId, tableService]);

  useEffect(() => {
    if (Object.values(currentTable).length > 0) {
      const { title, queryParams } = currentTable;
      const {
        columns = [],
        triggerIds: triggersIdsFetched = [],
        formId,
        divisionIds = [],
        locationIds: locationIdsFetched = [],
        locationGroupId: locationGroupIdFetched,
      } = queryParams || {};

      let locationValue: LocationFormValue = null;
      if (locationGroupIdFetched) {
        locationValue = { isAGroup: true, data: { id: locationGroupIdFetched } };
      } else if (locationIdsFetched?.length) {
        locationValue = {
          isAGroup: false,
          data: locationIdsFetched.map(id => ({ id })),
        };
      }

      if (tableColumns.length === columnsFixed.length) {
        buildColumnsFromDataFetched(columns as TableColumnState[], formId);
        form.setFieldsValue({
          title,
          formId,
          triggerIds: triggersIdsFetched,
          id: tableId,
          divisionId: divisionIds[0] ?? null,
          location: locationValue,
        });
        setTriggerIds(triggersIdsFetched);
        setSelectedEventTypeId(formId);
      }
    }
  }, [
    buildColumns,
    buildColumnsFromDataFetched,
    columnsFixed.length,
    currentTable,
    form,
    tableColumns.length,
    tableId,
  ]);

  useEffect(() => {
    if (selectedEventTypeId && orgId) {
      fetchEventsFromSchedules(orgId, divId, selectedEventTypeId);
    }
  }, [selectedEventTypeId, divId, fetchEventsFromSchedules, orgId]);

  return {
    form,
    mainLoading,
    tableColumns,
    selectedEventTypeId,
    triggerIds,
    eventTypes,
    eventLoading,
    divisionPerEventType,
    eventTypesFromSchedule,
    ColumnItem,
    buildBody,
    onChangeTriggers,
    handleFormIdChange,
    handleTriggersClear,
    onSortableMove,
    getImperativeHandle,
    divisions,
    handleDivisionChange,
  };
};

export default useTableForm;
